/**
 * KPICard — big number + optional trend sparkline + delta vs comparison period.
 *
 * Vertical-agnostic. Used everywhere a single scalar is the headline:
 *   - melt-shop "current bath temperature"
 *   - dashboards "active devices online"
 *   - billing "messages this month"
 *
 * Data shape: KPIValue (see ../types.ts).
 */

import type { ReactNode } from "react";
import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";
import { Card, CardContent } from "@/lib/ui/card";
import { cn } from "@/lib/ui/cn";
import { ChartSkeleton } from "../ChartSkeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/ui/tooltip";
import type { Citation, KPIValue, WidgetState } from "../types";
import { SparklineCell } from "../timeseries/SparklineCell";

export interface KPICardProps {
  /** Headline / metric name. */
  label: string;
  /** Optional secondary line under the label. */
  hint?: string;
  /** The metric value + optional trend + delta. */
  data: KPIValue;
  /** Optional citation rendered behind a tooltip on the value. */
  citation?: Citation;
  /** Widget state — default "ok". */
  state?: WidgetState;
  /** Optional class override for the card. */
  className?: string;
  /** Optional adornment to the right of the value (chip, icon). */
  trailing?: ReactNode;
}

export function KPICard({
  label,
  hint,
  data,
  citation,
  state = { kind: "ok" },
  className,
  trailing,
}: KPICardProps) {
  if (state.kind === "loading") {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <CardContent className="flex h-full min-h-0 flex-col gap-1 px-5 py-3">
          <div className="flex flex-col gap-0.5">
            <p className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
              {label}
            </p>
            {hint && (
              <p className="text-[11px] text-on-surface-muted">{hint}</p>
            )}
          </div>
          <ChartSkeleton shape="kpi" className="min-h-0 flex-1" />
        </CardContent>
      </Card>
    );
  }

  if (state.kind === "empty") {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <CardContent className="flex flex-col gap-1 pt-5">
          <p className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
            {label}
          </p>
          <p className="text-sm text-on-surface-muted">No data yet</p>
        </CardContent>
      </Card>
    );
  }

  if (state.kind === "error") {
    return (
      <Card className={cn("overflow-hidden border-danger/40", className)}>
        <CardContent className="flex flex-col gap-1 pt-5">
          <p className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
            {label}
          </p>
          <p className="text-sm text-danger">{state.message}</p>
        </CardContent>
      </Card>
    );
  }

  const valueDisplay =
    typeof data.value === "number" ? formatNumber(data.value) : data.value;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="flex h-full min-h-0 flex-col gap-1 px-5 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <p className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
              {label}
            </p>
            {hint && (
              <p className="text-[11px] text-on-surface-muted">{hint}</p>
            )}
          </div>
          {trailing}
        </div>

        <div className="flex items-baseline gap-1.5">
          {citation ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-3xl font-semibold tracking-tight text-on-surface">
                  {valueDisplay}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <span className="text-xs">
                  {citation.source}: <span className="font-mono">{citation.ref}</span>
                  {citation.derivation && (
                    <>
                      <br />
                      {citation.derivation}
                    </>
                  )}
                </span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-3xl font-semibold tracking-tight text-on-surface">
              {valueDisplay}
            </span>
          )}
          {data.unit && (
            <span className="text-sm font-medium text-on-surface-muted">
              {data.unit}
            </span>
          )}
        </div>

        {data.delta && (
          <DeltaRow delta={data.delta} />
        )}

        {data.trend && data.trend.length > 0 && (
          <div className="mt-1 min-h-0 flex-1">
            <SparklineCell
              points={data.trend}
              height="100%"
              referenceRange={data.delta ? undefined : undefined}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DeltaRow({ delta }: { delta: NonNullable<KPIValue["delta"]> }) {
  const Icon =
    delta.direction === "up"
      ? ArrowUp
      : delta.direction === "down"
        ? ArrowDown
        : ArrowRight;
  const color =
    delta.sentiment === "positive"
      ? "text-success"
      : delta.sentiment === "negative"
        ? "text-danger"
        : "text-on-surface-muted";

  return (
    <div className="flex items-center gap-1 text-xs">
      <span className={cn("inline-flex items-center gap-0.5 font-medium", color)}>
        <Icon className="size-3.5" />
        {formatNumber(Math.abs(delta.value))}
      </span>
      {delta.label && (
        <span className="text-on-surface-muted">{delta.label}</span>
      )}
    </div>
  );
}

function formatNumber(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return n.toFixed(2);
}
